import { createTheme } from '@mui/material/styles';
import { get as _get } from 'lodash';
import { ThemeType } from '../types/type';
import { afiChameleonTheme } from './afiChameleonTheme';

// 'Amfam' palette of colors
const chameleonTheme = afiChameleonTheme;
const termsAndConditionsLink = 'https://www.amfam.com/terms-and-conditions';
const primaryColor = chameleonTheme.data.palette.button.primaryActiveText;
const secondaryColor = chameleonTheme.data.palette.button.primaryActiveBackground;
const headerTopBorderColor = chameleonTheme.data.palette.button.primaryActiveBackground;
const headerBottomBorderColor = chameleonTheme.data.palette.button.primaryActiveText;
const listSelectionBackgroundColor = chameleonTheme.data.palette.other.nextStepsBg;
const footerTopBorderColor = chameleonTheme.data.palette.other.border;
const primaryButtonHoverColor = chameleonTheme.data.palette.button.primaryHoverBackground;
const accordionChevronColor = chameleonTheme.data.palette.button.primaryActiveBackground;
const radioButtonColor = chameleonTheme.data.palette.radio.defaultBorder;
const radioButtonSelectedColor = chameleonTheme.data.palette.radio.selectedRadio;
const bodyColor = chameleonTheme.data.palette.other.body;
const defaultBorderColor = chameleonTheme.data.palette.other.border;
const defaultBorderRadius = chameleonTheme.data.breakpoints.values.radiusDefault;
const defaultCheckBoxSize = chameleonTheme.data.breakpoints.values.sizeCheckbox;
const defaultRadioButtonSize = chameleonTheme.data.breakpoints.values.sizeRadio;
const mobileBreakPointFromFigma = _get(chameleonTheme, 'data.breakpoints.values.mobile', '');
const mobileBreakPoint = mobileBreakPointFromFigma.length > 0 ? mobileBreakPointFromFigma : '600px'; // If a mobile breakpoint is not defined in figma, use '600px' as a default value
const defaultBorderWidth = _get(chameleonTheme, 'data.breakpoints.values.borderWidthDefault', '1px');
const defaultInputHeight = _get(chameleonTheme, 'data.breakpoints.values.inputHeight', '48px');

const customStyle = {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: chameleonTheme.data.breakpoints.values.radiusButton,
          fontFamily: chameleonTheme.data.typography.fontFamily,
          '&.Mui-disabled': {
            color: chameleonTheme.data.palette.button.primaryDisabledText,
            backgroundColor: `${chameleonTheme.data.palette.button.primaryDisabledBackground}`,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: chameleonTheme.data.palette.button.primaryDisabledBorder,
          },
        },
        iconSizeMedium: {
          marginLeft: '0px',
          marginRight: '0px',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: chameleonTheme.data.palette.modal.backDropColor,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
            border: `${defaultBorderWidth} solid ${chameleonTheme.data.palette.other.border}`,
            height: defaultInputHeight,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: defaultInputHeight,
          borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-input': {
            height: defaultInputHeight,
            borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
          },
          '& fieldset': {
            height: defaultInputHeight,
            border: `${defaultBorderWidth} solid ${chameleonTheme.data.palette.other.border}`,
            borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: secondaryColor,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          paddingTop: '.3rem', //TODO: MuiInputLabel override is more of a work around than the best option. Need to come back to this later.
        },
      },
    },
  },
  palette: {
    primary: {
      main: `${secondaryColor}`,
    },
  },
  typography: {
    body1: {
      fontFamily: chameleonTheme.data.typography.fontFamily,
      fontWeight: chameleonTheme.data.typography.body1.fontWeight,
      fontSize: chameleonTheme.data.typography.body1.fontSize,
      color: chameleonTheme.data.palette.text.body,
    },
  },
  custom: {
    colors: {
      primaryColor: `${primaryColor}`,
      secondaryColor: `${secondaryColor}`,
      headerTopBorderColor: `${headerTopBorderColor}`,
      headerBottomBorderColor: `${headerBottomBorderColor}`,
      listSelectionBackgroundColor: `${listSelectionBackgroundColor}`,
      visitWebLinkColor: `${secondaryColor}`,
      footerTopBorderColor: `${footerTopBorderColor}`,
      primaryButtonHoverColor: `${primaryButtonHoverColor}`,
      accordionChevronColor: `${accordionChevronColor}`,
      radioButtonColor: `${radioButtonColor}`,
      radioButtonSelectedColor: `${radioButtonSelectedColor}`,
      bodyColor: `${bodyColor}`,
      defaultBorderColor: `${defaultBorderColor}`,
    },
    links: {
      termsAndConditionsLink: `${termsAndConditionsLink}`,
    },
    styles: {
      defaultRadioButtonSize: `${defaultRadioButtonSize}`,
      defaultCheckBoxSize: `${defaultCheckBoxSize}`,
      defaultBorderWidth: `${defaultBorderWidth}`,
      defaultInputHeight: `${defaultInputHeight}`,
      defaultBorderRadius: `${defaultBorderRadius}`,
      linearProgressBarStyle: {
        borderRadius: chameleonTheme.data.breakpoints.values.radiusModal,
        primaryBarColor: chameleonTheme.data.palette.progress.statusBarsFill, // Todo: To be updated with the correct Figma value once it becomes available.
        secondaryBarColor: chameleonTheme.data.palette.button.secondaryHoverBorder, // Todo: To be updated with the correct Figma value once it becomes available.
        header: {
          fontSize: chameleonTheme.data.typography.h4.fontSize,
          fontWeight: chameleonTheme.data.typography.h4.fontWeight,
          color: chameleonTheme.data.palette.modal.header,
        },
        description: {
          fontSize: chameleonTheme.data.typography.body1.fontSize,
          fontWeight: chameleonTheme.data.typography.body1.fontWeight,
          color: chameleonTheme.data.palette.modal.header,
        },
      },
      radioandcheckboxStyle: {
        defaultBackground: chameleonTheme.data.palette.radioandcheckbox.defaultBackground,
        defaultBorder: chameleonTheme.data.palette.radioandcheckbox.defaultBorder,
        hoverBackground: chameleonTheme.data.palette.radioandcheckbox.hoverBackground,
        hoverBorder: chameleonTheme.data.palette.radioandcheckbox.hoverBorder,
        selectedBackground: chameleonTheme.data.palette.radioandcheckbox.selectedBackground,
        selectedBorder: chameleonTheme.data.palette.radioandcheckbox.selectedBorder,
      },
      modalLoadingIndicatorStyle: {
        trackColor: chameleonTheme.data.palette.loading.spinnerTrack,
        spinnerColor: chameleonTheme.data.palette.loading.spinnerTrain,
        fontFamily: chameleonTheme.data.typography.modalHeader.fontFamily,
        fontSize: chameleonTheme.data.typography.loading.fontSize,
        fontWeight: chameleonTheme.data.typography.loading.fontWeight,
        color: chameleonTheme.data.palette.loading.spinnerText,
        backDropColor: chameleonTheme.data.palette.modal.backDropColor,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.loading[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.loading[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
        // },
      } as any,
      titleType1: {
        fontFamily: _get(chameleonTheme.data.typography.h1, 'fontFamily', chameleonTheme.data.typography.fontFamily),
        fontWeight: chameleonTheme.data.typography.h1.fontWeight,
        fontSize: chameleonTheme.data.typography.h1.fontSize,
        color: chameleonTheme.data.palette.text.h1,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.h1[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.h1[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
        // },
      } as any,
      titleType2: {
        fontFamily: _get(chameleonTheme.data.typography.h1, 'fontFamily', chameleonTheme.data.typography.fontFamily),
        fontWeight: chameleonTheme.data.typography.h1.fontWeight,
        fontSize: chameleonTheme.data.typography.h1.fontSize,
        color: chameleonTheme.data.palette.text.h1,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.h1[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.h1[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
        // },
      } as any,
      titleType3: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.h2.fontWeight,
        fontSize: chameleonTheme.data.typography.h2.fontSize,
        color: chameleonTheme.data.palette.text.h2,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.h2[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontWeight:
        //     chameleonTheme.data.typography.h2[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontWeight,
        //   fontSize:
        //     chameleonTheme.data.typography.h2[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
      },
      titleType4: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.h3.fontWeight,
        fontSize: chameleonTheme.data.typography.h3.fontSize,
        lineHeight: chameleonTheme.data.typography.h3.lineHeight,
        color: chameleonTheme.data.palette.text.h3,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.h3[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.h3[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
      },
      body2: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.body2.fontSize,
        fontWeight: chameleonTheme.data.typography.body2.fontWeight,
        letterSpacing: chameleonTheme.data.typography.body2.letterSpacing,
        lineHeight: chameleonTheme.data.typography.body2.lineHeight,
        textAlign: chameleonTheme.data.typography.body2.textAlign,
        color: chameleonTheme.data.palette.text.body,
      },
      body2Bold: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.body2Bold.fontSize,
        fontWeight: chameleonTheme.data.typography.body2Bold.fontWeight,
        letterSpacing: chameleonTheme.data.typography.body2Bold.letterSpacing,
        lineHeight: chameleonTheme.data.typography.body2Bold.lineHeight,
        textAlign: chameleonTheme.data.typography.body2Bold.textAlign,
        color: chameleonTheme.data.palette.text.body,
      },
      damagePicker: {
        hoverFill: chameleonTheme.data.palette.vdm.hoverFill,
        outline: chameleonTheme.data.palette.vdm.outline,
        selectedFill: chameleonTheme.data.palette.vdm.selectedFill,
        stroke: chameleonTheme.data.palette.vdm.stroke,
        text: chameleonTheme.data.palette.vdm.text,
      },
      adjusterNameStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.body1Bold.fontWeight,
        fontSize: chameleonTheme.data.typography.body1Bold.fontSize,
        color: chameleonTheme.data.palette.text.body,
      },
      adjusterShortNameStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.contactCardInitials.fontWeight,
        fontSize: chameleonTheme.data.typography.contactCardInitials.fontSize,
        color: chameleonTheme.data.palette.button.primaryActiveText,
      },
      adjusterCardPhoneNumberAndEmailStyle: {
        fontWeight: chameleonTheme.data.typography.link.fontWeight,
        fontSize: chameleonTheme.data.typography.link.fontSize,
        textDecoration: 'none',
        color: chameleonTheme.data.palette.link.default,
        fontFamily: chameleonTheme.data.typography.fontFamily,
        marginTop: '.5rem',
        marginLeft: '5rem',
        '&:hover': {
          color: chameleonTheme.data.palette.link.hover,
        },
      },
      adjusterCardPhoneNumberAndEmailCardStyle: {
        border: `solid ${defaultBorderWidth}`,
        borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
        borderColor: chameleonTheme.data.palette.other.border,
      },
      confirmationSuccessIcon: {
        color: chameleonTheme.data.palette.icon.success,
        height: '3em',
        width: '3em',
        fontSize: 16,
      },
      confirmationErrorIcon: {
        color: chameleonTheme.data.palette.icon.error,
        height: '50px',
        width: '50px',
      },
      confirmationWarningIcon: {
        color: chameleonTheme.data.palette.icon.warning,
        height: '50px',
        width: '50px',
      },
      pendingTasksBoxStyle: {
        backgroundColor: chameleonTheme.data.palette.other.nextStepsBg,
        padding: '1rem',
        fontSize: 16,
        borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
        border: `solid ${defaultBorderWidth}`,
        borderColor: chameleonTheme.data.palette.other.border,
      },
      errorWarningIconColor: chameleonTheme.data.palette.icon.warning,
      confirmationSubHeader: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        lineHeight: chameleonTheme.data.typography.h1.lineHeight,
        letterSpacing: chameleonTheme.data.typography.h1.letterSpacing,
        fontWeight: chameleonTheme.data.typography.h1.fontWeight,
        fontSize: chameleonTheme.data.typography.h1.fontSize,
        color: chameleonTheme.data.palette.text.h1,
        [`@media (max-width:${mobileBreakPoint})`]: {
          lineHeight: chameleonTheme.data.typography.h1["[baseTheme.breakpoints.down('md')]"].lineHeight,
          fontSize: chameleonTheme.data.typography.h1["[baseTheme.breakpoints.down('md')]"].fontSize,
        },
      } as any,
      accordionHeader: {
        fontSize: chameleonTheme.data.typography.accordionHeader.fontSize,
        fontWeight: chameleonTheme.data.typography.accordionHeader.fontWeight,
        letterSpacing: chameleonTheme.data.typography.accordionHeader.letterSpacing,
        lineHeight: chameleonTheme.data.typography.accordionHeader.lineHeight,
        textAlign: chameleonTheme.data.typography.accordionHeader.textAlign,
        color: chameleonTheme.data.palette.accordion.header,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.accordionHeader[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.accordionHeader[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
        // },
      } as any,
      accordionRowHeader: {
        fontSize: chameleonTheme.data.typography.accordionRow.fontSize,
        fontWeight: chameleonTheme.data.typography.accordionRow.fontWeight,
        letterSpacing: chameleonTheme.data.typography.accordionRow.letterSpacing,
        lineHeight: chameleonTheme.data.typography.accordionRow.lineHeight,
        textAlign: chameleonTheme.data.typography.accordionRow.textAlign,
        color: chameleonTheme.data.palette.accordion.row,
        elevation: 0,
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.accordionRow[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.accordionRow[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
        // },
      } as any,
      accordionRowGridItemStyle: {
        margin: '.5rem 0rem 0rem 0rem',
      },
      claimSummaryDetailItemStyle: {
        margin: '1rem',
      },
      confirmationList: {
        paddingLeft: '1.5em',
        listStyle: 'disc',
        paddingTop: '0px',
      },
      confirmationListElement: {
        display: 'list-item',
        paddingTop: '0px',
        paddingBottom: '0px',
      },
      serviceConfirmationHeader: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.h2.fontWeight,
        fontSize: chameleonTheme.data.typography.h2.fontSize,
        lineHeight: chameleonTheme.data.typography.h2.lineHeight,
        letterSpacing: chameleonTheme.data.typography.h2.letterSpacing,
        color: chameleonTheme.data.palette.text.h2,
        marginTop: '2em',
        // [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.h2[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontWeight:
        //     chameleonTheme.data.typography.h2[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontWeight,
        //   fontSize:
        //     chameleonTheme.data.typography.h2[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
      },
      primaryDescription: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.body1.fontWeight,
        fontSize: chameleonTheme.data.typography.body1.fontSize,
        lineHeight: chameleonTheme.data.typography.body1.lineHeight,
        color: chameleonTheme.data.palette.text.body,
      },
      secondaryDescription: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.formText.fontWeight,
        fontSize: chameleonTheme.data.typography.formText.fontSize,
        color: chameleonTheme.data.palette.text.body,
      },
      formLabelStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.formLabel.fontSize,
        fontWeight: chameleonTheme.data.typography.formLabel.fontWeight,
        letterSpacing: chameleonTheme.data.typography.formLabel.letterSpacing,
        lineHeight: chameleonTheme.data.typography.formLabel.lineHeight,
        textAlign: chameleonTheme.data.typography.formLabel.textAlign,
        color: chameleonTheme.data.palette.text.body,
      },
      formTextStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.formText.fontSize,
        fontWeight: chameleonTheme.data.typography.formText.fontWeight,
        letterSpacing: chameleonTheme.data.typography.formText.letterSpacing,
        lineHeight: chameleonTheme.data.typography.formText.lineHeight,
        textAlign: chameleonTheme.data.typography.formText.textAlign,
        color: chameleonTheme.data.palette.text.body,
      },
      disclaimerFont: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.body3.fontWeight,
        fontSize: chameleonTheme.data.typography.body3.fontSize,
        lineHeight: chameleonTheme.data.typography.body3.lineHeight,
        color: chameleonTheme.data.palette.text.disclaimer,
        [`@media (max-width:${mobileBreakPoint})`]: {
          lineHeight: _get(
            chameleonTheme.data.typography.body3["[baseTheme.breakpoints.down('md')]"],
            'lineHeight',
            chameleonTheme.data.typography.body3.lineHeight
          ),
          fontSize: _get(
            chameleonTheme.data.typography.body3["[baseTheme.breakpoints.down('md')]"],
            'fontSize',
            chameleonTheme.data.typography.body3.fontSize
          ),
        },
      } as any,
      monthShortNameFont: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.dateCircleMonth.fontWeight,
        fontSize: chameleonTheme.data.typography.dateCircleMonth.fontSize,
        color: chameleonTheme.data.palette.text.body,
      },
      claimSummaryDateFont: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.dateCircleDay.fontWeight,
        fontSize: chameleonTheme.data.typography.dateCircleDay.fontSize,
        color: chameleonTheme.data.palette.text.body,
      },
      errorSubcaseFont: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.body3.fontWeight,
        fontSize: chameleonTheme.data.typography.body3.fontSize,
        color: chameleonTheme.data.palette.text.error,
        [`@media (max-width:${mobileBreakPoint})`]: {
          lineHeight: _get(
            chameleonTheme.data.typography.body3["[baseTheme.breakpoints.down('md')]"],
            'lineHeight',
            chameleonTheme.data.typography.body3.lineHeight
          ),
          fontSize: _get(
            chameleonTheme.data.typography.body3["[baseTheme.breakpoints.down('md')]"],
            'fontSize',
            chameleonTheme.data.typography.body3.fontSize
          ),
        },
      } as any,
      providerTitleStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.body1.fontWeight,
        fontSize: chameleonTheme.data.typography.body1.fontSize,
        color: chameleonTheme.data.palette.text.body,
      },
      phoneNumberAndWebSiteStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.link.fontWeight,
        fontSize: chameleonTheme.data.typography.link.fontSize,
        color: `${secondaryColor}`,
        textDecoration: 'none',
        '&:hover': {
          color: chameleonTheme.data.palette.link.hover,
        },
        [`@media (max-width:${mobileBreakPoint})`]: {
          fontWeight: chameleonTheme.data.typography.link["[baseTheme.breakpoints.down('md')]"].fontWeight,
        },
      } as any,
      serviceProviderCardStyle: {
        borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
        borderColor: chameleonTheme.data.palette.other.border,
        border: `1px solid ${chameleonTheme.data.palette.other.border}`,
      },
      serviceProviderSelectedCardStyle: {
        borderRadius: chameleonTheme.data.breakpoints.values.radiusDefault,
        borderColor: chameleonTheme.data.palette.vdm.stroke,
        border: `${defaultBorderWidth} solid ${chameleonTheme.data.palette.vdm.stroke}`,
      },
      contactCardHeaderRowStyle: {
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontWeight: chameleonTheme.data.typography.body1Bold.fontWeight,
        fontSize: chameleonTheme.data.typography.body1Bold.fontSize,
        color: chameleonTheme.data.palette.text.body,
      },
      rentalAmountStyle: {
        fontFamily: _get(
          chameleonTheme.data.typography.rentalAmount,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        fontSize: chameleonTheme.data.typography.rentalAmount.fontSize,
        fontWeight: chameleonTheme.data.typography.rentalAmount.fontWeight,
        letterSpacing: chameleonTheme.data.typography.rentalAmount.letterSpacing,
        lineHeight: chameleonTheme.data.typography.rentalAmount.lineHeight,
        textAlign: chameleonTheme.data.typography.rentalAmount.textAlign,
        //  [`@media (max-width:${mobileBreakPoint})`]: {
        //   lineHeight:
        //     chameleonTheme.data.typography.rentalAmount[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].lineHeight,
        //   fontSize:
        //     chameleonTheme.data.typography.rentalAmount[
        //       "[baseTheme.breakpoints.down('md')]"
        //     ].fontSize,
        color: chameleonTheme.data.palette.text.rentalAmount,
      },
      editOnReviewStyle: {
        width: '100%',
        textAlign: 'end',
        marginRight: '0.5rem',
        textDecoration: 'underline',
        cursor: 'pointer',
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.link.fontSize,
        fontWeight: chameleonTheme.data.typography.link.fontWeight,
      },
      accordionHeaderButtonStyle: {
        backgroundColor: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        outline: 'none',
        textTransform: 'none',
        paddingLeft: '0px',
        marginTop: '1rem',
        marginLeft: '1rem',
        marginBottom: '1rem',
        '&.MuiButtonBase-root:hover': {
          bgcolor: 'white',
        },
      },
      themeType: {
        currentThemeType: ThemeType.AMFAM,
      },
      contactCardRowStyling: {
        backgroundColor: chameleonTheme.data.palette.other.nextStepsBg,
        padding: '0rem',
      },
      oddRow: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: chameleonTheme.data.palette.table.zebra1,
      },
      evenRow: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: chameleonTheme.data.palette.table.zebra2,
      },
      hrWrapperOdd: {
        background: '',
        flexBasis: '0%',
      },
      hrWrapperEven: {
        background: '',
        flexBasis: '0%',
      },
      hrStyle: `.5px solid ${chameleonTheme.data.palette.other.divider}`,
      ctaHrStyle: `1px solid ${chameleonTheme.data.palette.other.divider}`,
      hrDashedStyle: `1px solid ${chameleonTheme.data.palette.table.border}`,
      ctaNextButton: {
        order: 3,
        color: chameleonTheme.data.palette.button.primaryActiveText,
        backgroundColor: `${secondaryColor}`,
        marginLeft: '0px',
        minHeight: '55px',
        fontFamily: _get(
          chameleonTheme.data.typography.primaryButton,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
        textTransform: 'none',
        minWidth: '144px',
        [`@media (max-width:${mobileBreakPoint})`]: {
          width: '100%',
          order: 0,
          minWidth: '200px',
        },
        '&.Mui-disabled': {
          color: chameleonTheme.data.palette.button.primaryDisabledText,
          backgroundColor: `${chameleonTheme.data.palette.button.primaryDisabledBackground}`,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: chameleonTheme.data.palette.button.primaryDisabledBorder,
        },
      } as any,
      ctaBackButton: {
        order: 0,
        color: `${secondaryColor}`,
        backgroundColor: `${primaryColor}`,
        marginLeft: '1rem',
        textTransform: 'none',
        minHeight: '56px',
        minWidth: '100px',
        fontFamily: _get(
          chameleonTheme.data.typography.primaryButton,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
        border: '0px solid',
        [`@media (max-width:${mobileBreakPoint})`]: {
          order: 2,
          width: '100%',
          marginTop: '1rem',
          marginLeft: '0rem',
          border: '2px solid',
        },
      } as any,
      ctaSkipButton: {
        order: 2,
        color: `${secondaryColor}`,
        backgroundColor: chameleonTheme.data.palette.button.primaryActiveText,
        borderColor: `${secondaryColor}`,
        marginRight: '1rem',
        marginLeft: '1rem',
        minHeight: '56px',
        textDecoration: 'none',
        textTransform: 'none',
        fontFamily: _get(
          chameleonTheme.data.typography.primaryButton,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
        [`@media (max-width:${mobileBreakPoint})`]: {
          order: 2,
          width: '100%',
          marginTop: '1rem',
          marginRight: '0rem',
          marginLeft: '0rem',
        },
      } as any,
      ctaCancelButton: {
        order: 2,
        color: `${secondaryColor}`,
        backgroundColor: chameleonTheme.data.palette.button.primaryActiveText,
        textTransform: 'none',
        textDecoration: 'none',
        fontSize: chameleonTheme.data.typography.tertiaryButton.fontSize, // Only for CAH
        marginLeft: '1rem',
        marginRight: '1rem',
        fontWeight: chameleonTheme.data.typography.tertiaryButton.fontWeight,
        fontFamily: chameleonTheme.data.typography.fontFamily,
        '&:hover': {
          color: chameleonTheme.data.palette.link.hover,
          backgroundColor: 'transparent',
        },
        [`@media (max-width:${mobileBreakPoint})`]: {
          order: 3,
          marginTop: '1rem',
          width: '100%',
        },
      } as any,
      ctaFlexSpace: {
        order: 1,
        flexGrow: 1,
        [`@media (max-width:${mobileBreakPoint})`]: {
          flexGrow: 0,
        },
      } as any,
      ctaBackButtonIsLinkOnDesktop: true,
      ctaBackButtonIsLinkOnMobile: false,
      showReviewItemDividerLineOnMobile: true,
      chevronFirst: false,
      chevronHeight: '17px',
      chevronWidth: '20px',
      singleServiceSummaryHr: true,
      singleServiceSummaryDashedHr: false,
      activityIndicatorHasElevation: false,
      ctaErrorButton: {
        color: chameleonTheme.data.palette.button.primaryDestructiveActiveBackground,
        backgroundColor: chameleonTheme.data.palette.button.primaryActiveText,
        borderColor: chameleonTheme.data.palette.button.primaryDestructiveActiveBackground,
        textTransform: 'none',
        fontSize: chameleonTheme.data.typography.tertiaryButton.fontSize, // Only for CAH
        fontWeight: chameleonTheme.data.typography.tertiaryButton.fontWeight,
        fontFamily: chameleonTheme.data.typography.fontFamily,
      },
      ctaPrimaryErrorButton: {
        color: chameleonTheme.data.palette.button.primaryActiveText,
        backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveActiveBackground,
        borderColor: chameleonTheme.data.palette.button.primaryActiveText,
        fontSize: chameleonTheme.data.typography.primaryButton.fontSize, // Only for CAH
        fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
        fontFamily: _get(
          chameleonTheme.data.typography.primaryButton,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        letterSpacing: chameleonTheme.data.typography.primaryButton.letterSpacing,
        lineHeight: chameleonTheme.data.typography.primaryButton.lineHeight,
        textAlign: chameleonTheme.data.typography.primaryButton.textAlign,
      },
      errorButtonHoverColor: chameleonTheme.data.palette.button.primaryDestructiveHoverBackground,
      // Example usage  - Primary button(Yes, cancel this claim) of Cancel confirmation pop up
      genericPrimaryButton: {
        color: chameleonTheme.data.palette.button.primaryActiveText,
        backgroundColor: chameleonTheme.data.palette.button.primaryActiveBackground,
        borderColor: chameleonTheme.data.palette.button.primaryActiveBorder,
        border: '0px solid',
        textTransform: 'none',
        height: '56px',
        minWidth: '200px',
        fontFamily: _get(
          chameleonTheme.data.typography.primaryButton,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
        '&.MuiButtonBase-root:hover': {
          color: chameleonTheme.data.palette.button.primaryHoverText,
          backgroundColor: chameleonTheme.data.palette.button.primaryHoverBackground,
          borderColor: chameleonTheme.data.palette.button.primaryHoverBackground,
          border: '0px solid',
        },
        [`@media (max-width:${mobileBreakPoint})`]: {
          width: '100%',
        },
      } as any,
      // Example usage  - Error Banner 'Reload' button
      genericSecondaryButton: {
        color: chameleonTheme.data.palette.button.primaryActiveBackground,
        backgroundColor: 'white',
        borderColor: chameleonTheme.data.palette.button.primaryActiveBorder,
        border: '1px solid',
        textTransform: 'none',
        height: '56px',
        minWidth: '150px',
        fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
        '&.MuiButtonBase-root:hover': {
          color: chameleonTheme.data.palette.button.secondaryHoverText,
          backgroundColor: chameleonTheme.data.palette.button.secondaryHoverBackground,
          borderColor: chameleonTheme.data.palette.button.secondaryActiveBorder,
        },
        [`@media (max-width:${mobileBreakPoint})`]: {
          width: '100%',
        },
      } as any,
      // Example usage  - Tertiary button(No, continue this claim) of Cancel confirmation pop up
      genericTertiaryButton: {
        color: chameleonTheme.data.palette.button.secondaryActiveText,
        backgroundColor: 'white',
        borderColor: chameleonTheme.data.palette.button.primaryActiveBorder,
        textTransform: 'none',
        fontSize: chameleonTheme.data.typography.tertiaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.tertiaryButton.fontWeight,
        minHeight: '56px',
        '&:hover': {
          backgroundColor: 'inherit',
        },
      } as any,
      genericDestructiveTertiaryButton: {
        color: chameleonTheme.data.palette.button.tertiaryDestructiveActiveText,
        backgroundColor: 'white',
        borderColor: chameleonTheme.data.palette.button.primaryActiveBorder,
        textTransform: 'none',
        fontSize: chameleonTheme.data.typography.tertiaryButton.fontSize,
        fontWeight: chameleonTheme.data.typography.tertiaryButton.fontWeight,
        '&:hover': {
          backgroundColor: 'inherit',
        },
      },
      primaryDestructiveButton: {
        backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveActiveBackground,
        borderColor: chameleonTheme.data.palette.button.primaryDestructiveActiveBorder,
        color: chameleonTheme.data.palette.button.primaryDestructiveActiveText,
        hoverBackgroundColor: chameleonTheme.data.palette.button.primaryDestructiveHoverBackground,
        hoverTextColor: chameleonTheme.data.palette.button.primaryDestructiveHoverText,
      },
      secondaryDestructiveButton: {
        backGroundColor: chameleonTheme.data.palette.button.secondaryDestructiveActiveBackground,
        borderColor: chameleonTheme.data.palette.button.secondaryActiveBorder,
        border: '2px solid',
        textTransform: 'none',
        height: '56px',
        color: chameleonTheme.data.palette.button.secondaryDestructiveActiveText,
        hoverBackgroundColor: chameleonTheme.data.palette.button.secondaryDestructiveHoverBackground,
        hoverTextColor: chameleonTheme.data.palette.button.secondaryDestructiveHoverText,
      },

      modalHeader: {
        fontFamily: _get(
          chameleonTheme.data.typography.modalHeader,
          'fontFamily',
          chameleonTheme.data.typography.fontFamily
        ),
        fontSize: chameleonTheme.data.typography.modalHeader.fontSize,
        fontWeight: chameleonTheme.data.typography.modalHeader.fontWeight,
        letterSpacing: chameleonTheme.data.typography.modalHeader.letterSpacing,
        lineHeight: chameleonTheme.data.typography.modalHeader.lineHeight,
        textAlign: chameleonTheme.data.typography.modalHeader.textAlign,
        color: chameleonTheme.data.palette.modal.header,
        backgroundColor: chameleonTheme.data.palette.other.body,
        closeIconColor: chameleonTheme.data.palette.modal.closeIcon,
        [`@media (max-width:${mobileBreakPoint})`]: {
          // lineHeight:
          //   chameleonTheme.data.typography.modalHeader[
          //     "[baseTheme.breakpoints.down('md')]"
          //   ].lineHeight,
          // fontSize:
          //   chameleonTheme.data.typography.modalHeader[
          //     "[baseTheme.breakpoints.down('md')]"
          //   ].fontSize,
        },
      } as any,

      expandCollapseButton: {
        color: `${secondaryColor}`,
        backgroundColor: 'transparent',
        borderColor: chameleonTheme.data.palette.button.primaryActiveText,
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.link.fontSize,
        fontWeight: chameleonTheme.data.typography.link.fontWeight,
        textTransform: 'capitalize',
        textDecoration: 'underline',
        '&.MuiButtonBase-root:hover': {
          bgcolor: 'transparent',
        },
        [`@media (max-width:${mobileBreakPoint})`]: {
          fontWeight: chameleonTheme.data.typography.link["[baseTheme.breakpoints.down('md')]"].fontWeight,
        },
      } as any,
      expandCollapseDisabledButton: {
        color: chameleonTheme.data.palette.text.body,
        backgroundColor: 'transparent',
        borderColor: chameleonTheme.data.palette.button.primaryActiveText,
        fontFamily: chameleonTheme.data.typography.fontFamily,
        fontSize: chameleonTheme.data.typography.link.fontSize,
        fontWeight: chameleonTheme.data.typography.link.fontWeight,
        textTransform: 'capitalize',
        '&.MuiButtonBase-root:hover': {
          bgcolor: 'transparent',
        },
        [`@media (max-width:${mobileBreakPoint})`]: {
          fontWeight: chameleonTheme.data.typography.link["[baseTheme.breakpoints.down('md')]"].fontWeight,
        },
      } as any,
      endIcon: {
        marginLeft: '0px',
        marginRight: '0px',
      },
      goToClaimButtonGridStyle: {
        marginLeft: '0rem',
        paddingTop: '1rem',
        justifyContent: 'left',
        display: 'flex',
        [`@media (max-width:${mobileBreakPoint})`]: {
          justifyContent: 'center',
        },
      } as any,
      progressBar: {
        backgroundColor: chameleonTheme.data.palette.progress.statusBarsBackground,
        fillColor: chameleonTheme.data.palette.progress.statusBarsFill,
        progressTextColor: chameleonTheme.data.palette.progress.statusBarsProgressText,
        statusBarsProgressText: chameleonTheme.data.palette.progress.statusBarsProgressText,
        borderColor: chameleonTheme.data.palette.progress.statusBarsBorder,
      },
      progressStepperComplete: chameleonTheme.data.palette.progress.statusBarsFill,
      progressStepperSkipped: chameleonTheme.data.palette.progress.statusBarsSkipped,
      progressStepperBackground: chameleonTheme.data.palette.progress.statusBarsBackground,
      logoWidth: '122px',
      logoHeight: '46px',
      serviceProviderCardHr: `1px solid ${chameleonTheme.data.palette.other.divider}`,
      serviceProviderSummaryHr: `1px dashed ${chameleonTheme.data.palette.other.divider}`,
      singleServiceSummaryHrStyle: `1px solid ${chameleonTheme.data.palette.other.divider}`,
      modalHeaderHrStyle: {
        height: '1px',
        backgroundColor: chameleonTheme.data.palette.other.divider,
        border: '0 none',
        width: '100%',
      },
      listItemDividerHrStyle: {
        border: 'none', // Remove default border
        height: '1px',
        width: '100%',
        margin: 0,
        background: `repeating-linear-gradient(to right,
        ${chameleonTheme.data.palette.other.divider},
        ${chameleonTheme.data.palette.other.divider} 5px,
        transparent 5px,
        transparent 10px
  )`,
        boxSizing: 'border-box',
      },
      showButtonWhenServicesSkipped: true, // For CAH and AFI when all services are skipped 'Go to Claims' is a button. For MSA its a link.
      showRadioButtonAtTop: false,
      showGapBetweenCtaButtons: true, // Only for CAH, on service creation error screen, there is a horizontal space between CTA buttons
      showSmallRadioButton: true,
      showBorderAroundListItems: true,
      showUnderlinedClaimActivityHeader: true,
      showUnderlinedAccordionRowHeader: false,
      underLineModalHeader: true,
      showIconOnRisks: false,
      underLineModalDescription: false,
      showThickBorderForUnselectedListItem: true,
      showModalPopUpOnFNOLCancel: false,

      listItemBorderSelectedStyle: `${defaultBorderWidth} solid`,
      listItemBorderDefaultStyle: `${defaultBorderWidth} solid`,
      checkBox: {
        defaultBackground: chameleonTheme.data.palette.checkbox.defaultBackground,
        defaultBorder: chameleonTheme.data.palette.checkbox.defaultBorder,
        selectedBackground: chameleonTheme.data.palette.checkbox.selectedBackground,
        selectedBorder: chameleonTheme.data.palette.checkbox.selectedBorder,
        selectedCheckmark: chameleonTheme.data.palette.checkbox.selectedCheckmark,
      },
      borderRadius: {
        radiusDefault: chameleonTheme.data.breakpoints.values.radiusDefault,
        radiusInput: chameleonTheme.data.breakpoints.values.radiusInput,
        radiusModal: chameleonTheme.data.breakpoints.values.radiusModal,
        radiusProgressBar: chameleonTheme.data.breakpoints.values.radiusProgressBar,
      },
      genericModalAlertBoxStyle: {
        distributionDirection: 'row-reverse',
        contentJustifyType: 'flex-start',
        alignmentType: 'flex-end',
        spacing: 2,
        primaryActionButtonStyle: {
          color: chameleonTheme.data.palette.button.primaryActiveText,
          borderColor: chameleonTheme.data.palette.button.primaryActiveBorder,
          backgroundColor: chameleonTheme.data.palette.button.primaryActiveBackground,
          textTransform: 'capitalize',
          fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
          fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
          //TODO:fontFamily: chameleonTheme.data.typography.primaryButton.fontFamily,
          hoverBackgroundColor: chameleonTheme.data.palette.button.primaryHoverBackground,
          [`@media (max-width:${mobileBreakPoint})`]: {
            width: '100%',
          },
          '&.MuiButtonBase-root:hover': {
            color: chameleonTheme.data.palette.button.primaryActiveText,
            backgroundColor: chameleonTheme.data.palette.button.primaryHoverBackground,
            borderColor: chameleonTheme.data.palette.button.primaryActiveBorder,
          },
        },
        secondaryActionButtonStyle: {
          color: chameleonTheme.data.palette.button.secondaryActiveText,
          backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveActiveText,
          '&:hover': {
            backgroundColor: 'inherit',
          },
        },
      },
      genericModalDestructiveAlertBoxStyle: {
        distributionDirection: 'row',
        contentJustifyType: 'flex-end',
        alignmentType: 'flex-end',
        spacing: 0,
        primaryActionButtonStyle: {
          color: chameleonTheme.data.palette.button.primaryDestructiveActiveText,
          borderColor: chameleonTheme.data.palette.button.primaryDestructiveDisabledBorder,
          backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveActiveBackground,
          textTransform: 'capitalize',
          //TODO:fontFamily: chameleonTheme.data.typography.secondaryButton.fontFamily,
          fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
          fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
          buttonOrder: 2,
          width: 'auto',
          '&.MuiButtonBase-root:hover': {
            color: chameleonTheme.data.palette.button.primaryDestructiveHoverText,
            backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveHoverBackground,
            borderColor: chameleonTheme.data.palette.button.primaryDestructiveHoverBorder,
          },
        },
        secondaryActionButtonStyle: {
          color: chameleonTheme.data.palette.button.secondaryActiveText,
          backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveActiveText,
          '&:hover': {
            backgroundColor: 'inherit',
          },
        },
      },
      genericModalCancelAlertBoxStyle: {
        distributionDirection: 'row',
        contentJustifyType: 'flex-end',
        alignmentType: 'flex-end',
        spacing: 0,
        primaryActionButtonStyle: {
          color: chameleonTheme.data.palette.button.primaryDestructiveActiveText,
          borderColor: chameleonTheme.data.palette.button.primaryDestructiveDisabledBorder,
          backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveActiveBackground,
          textTransform: 'capitalize',
          //TODO:fontFamily: chameleonTheme.data.typography.secondaryButton.fontFamily,
          fontSize: chameleonTheme.data.typography.primaryButton.fontSize,
          fontWeight: chameleonTheme.data.typography.primaryButton.fontWeight,
          buttonOrder: 2,
          width: 'auto',
          '&.MuiButtonBase-root:hover': {
            color: chameleonTheme.data.palette.button.primaryDestructiveHoverText,
            backgroundColor: chameleonTheme.data.palette.button.primaryDestructiveHoverBackground,
            borderColor: chameleonTheme.data.palette.button.primaryDestructiveHoverBorder,
          },
        },
        secondaryActionButtonStyle: {
          color: chameleonTheme.data.palette.button.secondaryActiveText,
        },
      },
    },
    icons: {
      iconAuto: chameleonTheme.data.icons.iconAuto,
      iconProperty: chameleonTheme.data.icons.iconProperty,
      iconVinLocations: chameleonTheme.data.icons.graphicVinLocation,
    },
  },
};

export const afiChameleonFinalTheme = createTheme({
  ...customStyle,
});
